import { MouseEventHandler, ReactNode, useState } from 'react';

import { Stack, SxProps, Theme, useTheme } from '@mui/material';
import { ChevronDownIcon, MoreVerticalIcon } from 'icons';
import { partition } from 'lodash-es';

import { mergeSxProps } from '../../../theme';
import { Menu, MenuProps } from '../../Menu';
import { isMenuItemWithText } from '../../Menu/types';
import { IconButtonWithTooltip, IconButtonWithTooltipProps } from '../IconButtonWithTooltip';

import type { IconButtonVariant } from '../IconButton/types';

type Props = Omit<IconButtonWithTooltipProps, 'title' | 'ref'> &
  Pick<MenuProps, 'anchorOrigin' | 'transformOrigin' | 'items' | 'activeIndex'> & {
    'data-testid'?: string;
    menuSx?: SxProps<Theme> | undefined;
    variant?: IconButtonVariant;
    Icon?: ReactNode;
    title?: string;
    showChevron?: boolean;
    onOpenChange?(value: boolean): void;
    inlineSingleItem?: boolean;
  };

export const ButtonMenu = ({
  anchorOrigin,
  transformOrigin,
  menuSx,
  isLoading,
  size,
  variant = 'text',
  Icon = <MoreVerticalIcon />,
  color = variant === 'text' ? 'text' : 'light',
  title = 'More actions',
  TooltipProps,
  showChevron,
  sx,
  disabled,
  onOpenChange,
  items,
  inlineSingleItem,
  ...props
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const testId = props['data-testid'] || 'button-menu';
  const { palette } = useTheme();

  /**
   * Only send an `sx` when it would actually carry something.
   *
   * This used to be unconditional, and in the common case (light theme, no chevron, no caller `sx`)
   * it resolved to nothing at all — `{ width: undefined }`. Harmless under MUI, but `IconButton`'s
   * visual-refresh branch refuses any `sx` on principle, because a Tailwind component would silently
   * drop it. An always-present empty `sx` therefore pinned this button to MUI while the inline
   * actions beside it became Design System circles — a visibly mismatched pair.
   *
   * `showChevron` still keeps this button on MUI, and correctly so: icon-plus-chevron is not an
   * icon-only button, which is the only shape the DS component can express.
   */
  const needsSx = showChevron || palette.mode === 'dark' || !!sx;

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    // Prevent default too: when this menu lives inside a link/anchor (e.g. a clickable card),
    // stopPropagation alone blocks the router's onClick but not the browser's native <a>
    // navigation, so the page would navigate instead of opening the menu.
    event.preventDefault();
    setAnchor(event.currentTarget);
    onOpenChange?.(true);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const [inlineActions, moreActions] =
    inlineSingleItem && items.length === 1 ? [items, []] : partition(items, 'inline');

  if (!items.length) {
    return null;
  }

  return (
    <>
      {inlineActions.filter(isMenuItemWithText).map((action, index) => (
        <IconButtonWithTooltip
          key={`inline-action-${index}`}
          title={action.text}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            action.onClick?.();
          }}
          disabled={disabled}
          size={size}
          variant={variant}
          color={action.severity === 'danger' ? 'error' : color}
          isLoading={isLoading}
          sx={sx}
        >
          {action.icon}
        </IconButtonWithTooltip>
      ))}
      {!!moreActions.length && (
        <>
          <IconButtonWithTooltip
            disabled={disabled}
            onClick={handleClick}
            variant={variant}
            size={size}
            className={anchor ? 'active' : undefined}
            color={color}
            title={title}
            TooltipProps={{ placement: 'bottom-end', ...TooltipProps }}
            isLoading={isLoading}
            data-testid={testId}
            sx={
              needsSx
                ? mergeSxProps(
                    ({ palette: themePalette }) => ({
                      width: showChevron ? 'initial' : undefined,
                      ...(themePalette.mode === 'dark' && {
                        color: themePalette.common.white,
                      }),
                    }),
                    sx
                  )
                : undefined
            }
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              {Icon}
              {showChevron && <ChevronDownIcon />}
            </Stack>
          </IconButtonWithTooltip>

          <Menu
            sx={menuSx}
            anchor={anchor}
            onClose={handleClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            onTransitionExited={() => onOpenChange?.(false)}
            items={moreActions}
            {...props}
          />
        </>
      )}
    </>
  );
};
